import { useMemo, useState } from "react";
import { IconChevronDown, IconX } from "@tabler/icons-react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ProductPickerItem {
  id: string;
  title: string;
  image: string | null;
  price?: string | number;
}

interface ProductMultiSelectProps {
  products: ProductPickerItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  optional?: boolean;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  className?: string;
}

/**
 * Pick several products. The list opens in a portalled popover the width of
 * the control, so a card's overflow never clips it.
 */
export default function ProductMultiSelect({
  products,
  selectedIds,
  onChange,
  loading = false,
  disabled = false,
  optional = false,
  required = false,
  placeholder = "Choose products",
  helperText,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  className = "",
}: ProductMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? products.filter((p) => p.title.toLowerCase().includes(q)) : products;
  }, [products, search]);

  const allSelected = products.length > 0 && selectedIds.length === products.length;

  const summary = loading
    ? "Loading products"
    : selectedIds.length === 0
      ? optional
        ? "All products"
        : placeholder
      : allSelected
        ? "All products selected"
        : `${selectedIds.length} ${selectedIds.length === 1 ? "product" : "products"} selected`;

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const selectAll = () => onChange(allSelected ? [] : products.map((p) => p.id));

  const chips = selectedIds
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is ProductPickerItem => Boolean(p));

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled || loading}
            aria-haspopup="listbox"
            aria-expanded={open}
            className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={cn("truncate", selectedIds.length === 0 && "text-muted-foreground")}>{summary}</span>
            <IconChevronDown size={16} stroke={1.75} className="shrink-0 text-muted-foreground" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-[--radix-popover-trigger-width] rounded-lg p-0 shadow-lg">
          <div className="border-b border-border p-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              autoFocus
              className="h-8"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {products.length > 0 && !search && (
              <label className="flex h-9 cursor-pointer items-center gap-2.5 px-3 text-xs font-medium text-muted-foreground hover:bg-muted/60">
                <Checkbox checked={allSelected} onCheckedChange={selectAll} />
                Select all ({products.length})
              </label>
            )}
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">No products match</p>
            ) : (
              filtered.map((product) => (
                <label
                  key={product.id}
                  className="flex h-10 cursor-pointer items-center gap-2.5 px-3 text-sm hover:bg-muted/60"
                >
                  <Checkbox checked={selectedIds.includes(product.id)} onCheckedChange={() => toggle(product.id)} />
                  <img
                    src={product.image ?? "/placeholder-product.png"}
                    alt=""
                    className="size-7 shrink-0 rounded bg-muted object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/placeholder-product.png";
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{product.title}</span>
                  {product.price !== undefined && (
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">${product.price}</span>
                  )}
                </label>
              ))
            )}
          </div>
          {hasMore && onLoadMore && (
            <div className="border-t border-border px-3 py-2">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                {loadingMore ? "Loading" : "Load more"}
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.slice(0, 6).map((product) => (
            <span
              key={product.id}
              className="inline-flex items-center gap-1 rounded-md border border-border py-0.5 pl-2 pr-1 text-xs text-foreground"
            >
              <span className="max-w-[12rem] truncate">{product.title}</span>
              <button
                type="button"
                onClick={() => toggle(product.id)}
                disabled={disabled}
                aria-label={`Remove ${product.title}`}
                className="inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <IconX size={12} stroke={2} />
              </button>
            </span>
          ))}
          {chips.length > 6 && <span className="self-center text-xs text-muted-foreground">+{chips.length - 6} more</span>}
        </div>
      )}

      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      {required && selectedIds.length === 0 && <p className="text-xs text-destructive">Pick at least one product</p>}
    </div>
  );
}
